import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../../../theme';
import { renderInstruction } from '../components/toolRefs';
import ControlledStationRun from './ControlledStationRun';
import { usesControlledExecution } from './controlledRunHelpers';

const API_BASE = process.env.REACT_APP_API_URL;
const LS_KEY = 'stationOperate.assignment'; // { flowId, stationId, productNumber }

const TYPE_COLORS = {
  'Raw Material': '#a78bfa', 'Assembly': '#60a5fa', 'Firmware Flash': '#34d399',
  'Functional Test': '#46BB89', 'Burn-In': '#f59e0b', 'Visual Inspection': '#38bdf8',
  'Quality Gate': '#EC2124', 'Semi-Finished Goods': '#94a3b8', 'Packaging': '#fb923c',
  'Rework': '#ef4444', 'Hold': '#dc2626', 'Release / Shipping': '#10b981',
  'Finished Goods': '#22c55e', 'Generic': '#64748b'
};

const fmtCycle = sec => {
  if (!sec) return null;
  if (sec >= 3600) return `${Math.round(sec / 360) / 10} h`;
  if (sec >= 60) return `${Math.round(sec / 60)} min`;
  return `${sec} s`;
};

// Normalise a code for matching scanned input to an expected part number.
const norm = v => String(v || '').toUpperCase().replace(/\s+/g, '');

const page = {
  minHeight: '100vh', background: colors.page, color: colors.textBody,
  display: 'flex', flexDirection: 'column'
};
const bar = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '0.7rem 1.2rem',
  borderBottom: `1px solid ${colors.panelBorder}`, background: colors.pageAlt, flexWrap: 'wrap'
};
const btn = (bg, fg) => ({
  background: bg, color: fg, border: 'none', borderRadius: 10,
  padding: '0.7rem 1.4rem', fontWeight: 800, fontSize: '1rem', cursor: 'pointer'
});
const card = {
  background: colors.surface, border: `1px solid ${colors.panelBorder}`,
  borderRadius: 14, padding: '1rem 1.2rem'
};
const sectionTitle = {
  fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: colors.muted, margin: '0 0 0.6rem'
};

export default function StationOperate() {
  const { flowId: paramFlowId, stationId: paramStationId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [assignment, setAssignment] = useState(null); // { flowId, stationId, productNumber }
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Boot: resolve assignment from URL params or localStorage ───────────────
  useEffect(() => {
    if (paramFlowId && paramStationId) {
      setAssignment({ flowId: paramFlowId, stationId: paramStationId, productNumber: '' });
      return;
    }
    if (paramFlowId) { setAssignment(false); return; }
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (saved && saved.flowId && saved.stationId) setAssignment(saved);
      else setAssignment(false); // explicit "no assignment → setup"
    } catch { setAssignment(false); }
  }, [paramFlowId, paramStationId]);

  // ── Load the flow once we know which one ───────────────────────────────────
  useEffect(() => {
    if (!assignment || !assignment.flowId) { if (assignment === false) setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setFlow(null);
      setError('');
      try {
        const token = await getToken();
        const revisionQuery = assignment.flowRevision ? `?revision=${encodeURIComponent(assignment.flowRevision)}` : '';
        const res = await fetch(`${API_BASE}/quality/process-flows/${assignment.flowId}${revisionQuery}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.ok && (!assignment.flowRevision || data.flow?.currentRevision === assignment.flowRevision)) setFlow({ ...data.flow, availableRevisions: data.availableRevisions || [] });
        else setError(data.error || 'Failed to load flow');
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [assignment, getToken]);

  const saveAssignment = a => {
    localStorage.setItem(LS_KEY, JSON.stringify(a));
    setAssignment(a);
  };
  const clearAssignment = () => {
    localStorage.removeItem(LS_KEY);
    setAssignment(false);
    setFlow(null);
    navigate('/operate', { replace: true });
  };

  if (loading || assignment === null) {
    return <div style={{ ...page, alignItems: 'center', justifyContent: 'center' }}>Loading…</div>;
  }

  // Setup mode: no valid assignment, or the station no longer exists on the flow.
  const station = flow && assignment ? (flow.stations || []).find(s => s.stationId === assignment.stationId) : null;
  if (!assignment || (flow && !station && !usesControlledExecution(flow))) {
    return <SetupView key={paramFlowId || 'setup'} preselectFlowId={paramFlowId} onConfirm={saveAssignment} getToken={getToken} error={error} />;
  }
  if (!flow) {
    return (
      <div style={{ ...page, alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <div style={{ color: colors.danger }}>{error || 'Flow unavailable.'}</div>
        <button onClick={clearAssignment} style={btn(colors.surfaceRaised, colors.textBody)}>Change station</button>
      </div>
    );
  }

  if (usesControlledExecution(flow)) {
    return <ControlledStationRun
      key={`${flow._id}:${assignment.stationId}`}
      flow={flow}
      station={station || { stationId: assignment.stationId, name: assignment.stationId }}
      assignment={assignment}
      onChangeStation={clearAssignment}
      onChangeProduct={pn => saveAssignment({ ...assignment, productNumber: pn })}
      getToken={getToken}
    />;
  }

  return (
    <OperateView
      flow={flow}
      station={station}
      assignment={assignment}
      onChangeStation={clearAssignment}
      onChangeProduct={pn => saveAssignment({ ...assignment, productNumber: pn })}
      getToken={getToken}
    />
  );
}

// ── Setup: pick the flow, product and station this PC will operate ───────────
function SetupView({ preselectFlowId, onConfirm, getToken, error: bootError }) {
  const [flows, setFlows] = useState([]);
  const [flowId, setFlowId] = useState(preselectFlowId || '');
  const [flow, setFlow] = useState(null);
  const [productNumber, setProductNumber] = useState('');
  const [stationId, setStationId] = useState('');
  const [flowRevision, setFlowRevision] = useState('');
  const [revisionLoading, setRevisionLoading] = useState(false);
  const [err, setErr] = useState(bootError || '');
  const selectionGeneration = useRef(0);

  useEffect(() => () => { selectionGeneration.current += 1; }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/quality/process-flows`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!cancelled) {
          if (!res.ok || !data.ok) throw new Error(data.error || 'Could not load process flows');
          setFlows(data.flows || []);
        }
      } catch (e) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  useEffect(() => {
    const generation = ++selectionGeneration.current;
    setFlow(null); setFlowRevision(''); setStationId(''); setProductNumber(''); setErr('');
    setRevisionLoading(Boolean(flowId));
    if (!flowId) return undefined;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/quality/process-flows/${flowId}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (generation !== selectionGeneration.current) return;
        if (!res.ok || !data.ok) throw new Error(data.error || 'Could not load process flow');
        setFlow({ ...data.flow, availableRevisions: data.availableRevisions || [] });
        setProductNumber((data.flow.productNumbers || [])[0] || '');
      } catch (e) { if (generation === selectionGeneration.current) setErr(e.message); }
      finally { if (generation === selectionGeneration.current) setRevisionLoading(false); }
    })();
    return () => { if (generation === selectionGeneration.current) selectionGeneration.current += 1; };
  }, [flowId, getToken]);

  const selectRevision = async revision => {
    const generation = ++selectionGeneration.current;
    setFlowRevision(revision); setStationId(''); setErr('');
    setRevisionLoading(Boolean(revision));
    if (!revision) return;
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/quality/process-flows/${flowId}?revision=${encodeURIComponent(revision)}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (generation !== selectionGeneration.current) return;
      if (!res.ok || !data.ok || data.flow?.currentRevision !== revision) throw new Error(data.error || 'Could not load exact released flow revision');
      setFlow({ ...data.flow, availableRevisions: data.availableRevisions || [] });
      setProductNumber((data.flow.productNumbers || [])[0] || '');
    } catch (e) { if (generation === selectionGeneration.current) setErr(e.message); }
    finally { if (generation === selectionGeneration.current) setRevisionLoading(false); }
  };
  const controlled = flow && usesControlledExecution(flow);
  const releasedRevisions = flow?.availableRevisions?.length ? flow.availableRevisions : (flow?.currentStatus === 'Released' ? [{ revision: flow.currentRevision }] : []);
  const selectionReady = Boolean(flowId && stationId && !revisionLoading && (!controlled || flowRevision) && !err);

  const sel = {
    background: colors.surface, border: `1px solid ${colors.panelBorder}`, color: colors.textBody,
    borderRadius: 10, padding: '0.7rem 0.9rem', fontSize: '1rem', width: '100%', boxSizing: 'border-box'
  };

  return (
    <div style={{ ...page, alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ ...card, width: 'min(560px, 94vw)' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.1em', color: colors.primary, textTransform: 'uppercase' }}>
          Station Operate
        </div>
        <h1 style={{ margin: '0.3rem 0 0.2rem', fontSize: '1.6rem' }}>Set up this workstation</h1>
        <p style={{ color: colors.muted, marginTop: 0, fontSize: '0.9rem' }}>
          Choose the production line, product and station. This PC will remember it and open straight into that station next time.
        </p>

        <div style={{ display: 'grid', gap: '0.9rem', marginTop: '1rem' }}>
          <label>
            <div style={sectionTitle}>Production line</div>
            <select value={flowId} onChange={e => { selectionGeneration.current += 1; setFlowId(e.target.value); }} style={sel}>
              <option value="">Select a process flow…</option>
              {flows.map(f => (
                <option key={f._id} value={f._id}>
                  {f.documentNumber} — {f.name}{f.productFamily ? ` (${f.productFamily})` : ''}{f.currentStatus !== 'Released' ? ` · ${f.currentStatus}` : ''}
                </option>
              ))}
            </select>
          </label>

          {controlled && <label style={{ display: 'grid', gap: 6 }}>Released flow revision
            <select aria-label="Released flow revision" value={flowRevision} disabled={revisionLoading} onChange={event => selectRevision(event.target.value)} style={sel}>
              <option value="">Select an exact released revision</option>
              {releasedRevisions.map(item => <option key={item.revision} value={item.revision}>{item.revision}</option>)}
            </select>
            <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>Select the released revision before choosing the production station. Draft changes do not replace its content.</span>
          </label>}

          {flow && (flow.productNumbers || []).length > 0 && (
            <label>
              <div style={sectionTitle}>Product</div>
              <select value={productNumber} disabled={revisionLoading || (controlled && !flowRevision)} onChange={e => setProductNumber(e.target.value)} style={sel}>
                {(flow.productNumbers || []).map(pn => <option key={pn} value={pn}>{pn}</option>)}
              </select>
            </label>
          )}

          {flow && (
            <label>
              <div style={sectionTitle}>Station</div>
              <select value={stationId} disabled={revisionLoading || (controlled && !flowRevision)} onChange={e => setStationId(e.target.value)} style={sel}>
                <option value="">Select a station…</option>
                {(flow.stations || []).map(s => (
                  <option key={s.stationId} value={s.stationId}>{s.stationId} — {s.name} ({s.type})</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {err && <div style={{ color: colors.danger, marginTop: 12, fontSize: '0.85rem' }}>{err}</div>}

        <button
          disabled={!selectionReady}
          onClick={() => onConfirm({ flowId, stationId, productNumber, ...(controlled ? { flowRevision } : {}) })}
          style={{ ...btn(colors.primary, '#06140e'), marginTop: '1.2rem', width: '100%', opacity: !selectionReady ? 0.5 : 1 }}
        >
          Start operating this station
        </button>
      </div>
    </div>
  );
}

// ── Operate: the live station kiosk ──────────────────────────────────────────
function OperateView({ flow, station, assignment, onChangeStation, onChangeProduct, getToken }) {
  const color = TYPE_COLORS[station.type] || TYPE_COLORS.Generic;
  const expectedParts = useMemo(() => station.parts || [], [station.parts]);

  const blankMaterials = useCallback(
    () => expectedParts.map((p, i) => ({
      id: `${p.partNumber || 'part'}-${i}`,
      partNumber: p.partNumber || '', name: p.name || '',
      quantity: p.quantity == null ? 1 : p.quantity, unit: p.unit || 'pcs',
      confirmed: false, scannedValue: ''
    })),
    [expectedParts]
  );

  const [serial, setSerial] = useState('');
  const [materials, setMaterials] = useState(blankMaterials);
  const [scan, setScan] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [todayCount, setTodayCount] = useState(null);
  const [lastSerial, setLastSerial] = useState('');
  const [assemblyDetails, setAssemblyDetails] = useState([]);
  const startedAtRef = useRef(new Date().toISOString());
  const scanRef = useRef(null);

  const assemblyRefs = useMemo(() => station.assemblyInstructions || [], [station.assemblyInstructions]);
  useEffect(() => {
    if (!assemblyRefs.length) { setAssemblyDetails([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const results = await Promise.all(assemblyRefs.map(r =>
          fetch(`${API_BASE}/quality/assembly-instructions/${r.refId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.json()).then(d => (d.ok ? d.instruction : null)).catch(() => null)
        ));
        if (!cancelled) setAssemblyDetails(results.filter(Boolean));
      } catch { if (!cancelled) setAssemblyDetails([]); }
    })();
    return () => { cancelled = true; };
  }, [assemblyRefs, getToken]);

  const refreshCount = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/quality/station-runs?flowId=${flow._id}&stationId=${station.stationId}&limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) setTodayCount(data.todayCount);
    } catch { /* non-fatal */ }
  }, [flow._id, station.stationId, getToken]);

  useEffect(() => { refreshCount(); }, [refreshCount]);

  const applyScan = value => {
    const v = (value || '').trim();
    if (!v) return;
    const target = norm(v);
    // Match the scanned code to the first unconfirmed expected part.
    const idx = materials.findIndex(m => !m.confirmed && m.partNumber && (
      norm(m.partNumber) === target || target.includes(norm(m.partNumber)) || norm(m.partNumber).includes(target)
    ));
    if (idx >= 0) {
      setMaterials(prev => prev.map((m, i) => i === idx ? { ...m, confirmed: true, scannedValue: v } : m));
      setScanMsg(`✓ ${materials[idx].name || materials[idx].partNumber}`);
    } else if (!serial) {
      // No part matched and no serial yet → treat the scan as the unit serial.
      setSerial(v);
      setScanMsg(`Unit serial set: ${v}`);
    } else {
      setScanMsg(`No expected part matches "${v}". Tap a row to confirm manually.`);
    }
    setScan('');
    if (scanRef.current) scanRef.current.focus();
  };

  const toggle = id => setMaterials(prev => prev.map(m => m.id === id
    ? { ...m, confirmed: !m.confirmed, scannedValue: m.confirmed ? '' : (m.scannedValue || 'manual') }
    : m));

  const allConfirmed = materials.every(m => m.confirmed);
  const confirmedCount = materials.filter(m => m.confirmed).length;

  const complete = async (status) => {
    if (!serial.trim()) { setScanMsg('Scan or enter the unit serial first.'); return; }
    if (status === 'completed' && !allConfirmed) { setScanMsg('Confirm all required materials before completing this unit.'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/quality/station-runs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          flowId: flow._id, stationId: station.stationId, unitSerial: serial.trim(),
          productNumber: assignment.productNumber || '', status,
          startedAt: startedAtRef.current,
          scannedMaterials: materials.map(({ partNumber, name, quantity, unit, confirmed, scannedValue }) =>
            ({ partNumber, name, quantity, unit, confirmed, scannedValue }))
        })
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `Save failed (${res.status})`);
      setLastSerial(serial.trim());
      setSerial('');
      setMaterials(blankMaterials());
      setScanMsg('');
      startedAtRef.current = new Date().toISOString();
      refreshCount();
      if (scanRef.current) scanRef.current.focus();
    } catch (e) { setScanMsg(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={page}>
      {/* Header */}
      <div style={bar}>
        <span style={{ width: 12, height: 12, borderRadius: 4, background: color, flexShrink: 0 }} />
        <span style={{ fontFamily: 'monospace', color: colors.muted }}>{flow.documentNumber} · Rev {flow.currentRevision}</span>
        <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>{station.stationId} — {station.name}</span>
        <span style={{ fontSize: '0.85rem', color: colors.muted }}>{station.type}{fmtCycle(station.estimatedCycleTimeSeconds) ? ` · target ${fmtCycle(station.estimatedCycleTimeSeconds)}` : ''}</span>
        <div style={{ flex: 1 }} />
        {assignment.productNumber && <span style={{ fontSize: '0.85rem', color: colors.textBody }}>Product: <strong>{assignment.productNumber}</strong></span>}
        {todayCount != null && (
          <span style={{ background: 'rgba(70,187,137,0.15)', color: colors.primary, borderRadius: 999, padding: '0.3rem 0.8rem', fontWeight: 800 }}>
            {todayCount} today
          </span>
        )}
        <button onClick={onChangeStation} style={{ ...btn(colors.surfaceRaised, colors.textBody), padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
          Change station
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)', gap: '1rem', padding: '1rem', alignItems: 'start' }}>
        {/* LEFT: build the unit */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={card}>
            <div style={sectionTitle}>Unit serial</div>
            <input
              ref={scanRef}
              value={scan}
              onChange={e => setScan(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyScan(scan); } }}
              autoFocus
              placeholder="Scan a barcode or type, then Enter — scans the serial, then each material"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: '1.15rem', padding: '0.8rem 1rem',
                background: colors.pageAlt, border: `2px solid ${color}`, borderRadius: 12, color: colors.textStrong, fontFamily: 'monospace' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <span style={{ color: colors.muted, fontSize: '0.85rem' }}>Serial:</span>
              <input value={serial} onChange={e => setSerial(e.target.value)} placeholder="(none yet)"
                style={{ flex: 1, fontFamily: 'monospace', fontSize: '1.05rem', padding: '0.5rem 0.7rem',
                  background: colors.surface, border: `1px solid ${colors.panelBorder}`, borderRadius: 8, color: colors.textStrong }} />
            </div>
            {scanMsg && <div style={{ marginTop: 8, fontSize: '0.9rem', color: scanMsg.startsWith('✓') || scanMsg.startsWith('Unit') ? colors.primary : colors.statusYellow }}>{scanMsg}</div>}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={sectionTitle}>Materials to scan in</div>
              <span style={{ color: allConfirmed ? colors.primary : colors.muted, fontSize: '0.85rem', fontWeight: 700 }}>
                {confirmedCount}/{materials.length} confirmed
              </span>
            </div>
            {materials.length === 0 && <div style={{ color: colors.muted, fontSize: '0.9rem' }}>No materials defined for this station.</div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {materials.map(m => (
                <button key={m.id} onClick={() => toggle(m.id)}
                  style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    background: m.confirmed ? 'rgba(70,187,137,0.12)' : colors.pageAlt,
                    border: `1px solid ${m.confirmed ? 'rgba(70,187,137,0.4)' : colors.panelBorder}`,
                    borderRadius: 10, padding: '0.55rem 0.7rem', color: colors.textBody }}>
                  <span style={{ fontSize: '1.1rem', color: m.confirmed ? colors.primary : colors.muted }}>{m.confirmed ? '☑' : '☐'}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#60a5fa', minWidth: 110 }}>{m.partNumber || '—'}</span>
                  <span style={{ flex: 1, fontSize: '0.88rem' }}>{m.name}</span>
                  <span style={{ color: colors.muted, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{m.quantity} {m.unit}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button disabled={saving || !serial.trim() || !allConfirmed} onClick={() => complete('completed')}
              style={{ ...btn(colors.primary, '#06140e'), flex: 2, opacity: (saving || !serial.trim() || !allConfirmed) ? 0.5 : 1 }}>
              {saving ? 'Saving…' : allConfirmed ? '✓ Complete unit' : `Complete (${confirmedCount}/${materials.length})`}
            </button>
            <button disabled={saving || !serial.trim()} onClick={() => complete('failed')}
              style={{ ...btn('rgba(236,33,36,0.15)', colors.danger), flex: 1, opacity: (saving || !serial.trim()) ? 0.5 : 1 }}>
              Mark failed
            </button>
          </div>
          {lastSerial && <div style={{ color: colors.muted, fontSize: '0.85rem' }}>Last recorded: <strong style={{ color: colors.textBody }}>{lastSerial}</strong></div>}
        </div>

        {/* RIGHT: how to do this step */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          {station.notes && (
            <div style={card}>
              <div style={sectionTitle}>Work instructions</div>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.92rem' }}>{station.notes}</div>
            </div>
          )}

          {assemblyRefs.length > 0 && (
            <div style={card}>
              <div style={sectionTitle}>Assembly instructions</div>
              {assemblyDetails.length === 0 ? (
                <Resource label="Instruction" items={station.assemblyInstructions} />
              ) : assemblyDetails.map(ai => (
                <div key={ai._id} style={{ marginBottom: '0.9rem' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 6 }}>
                    {ai.documentNumber} — {ai.name}
                    {ai.estimatedMinutes ? <span style={{ color: colors.muted, fontWeight: 400 }}> · {ai.estimatedMinutes} min</span> : null}
                  </div>
                  {(ai.steps || []).length === 0
                    ? <div style={{ color: colors.muted, fontSize: '0.85rem' }}>No steps defined.</div>
                    : (ai.steps || []).map((st, i) => (
                      <div key={i} style={{ fontSize: '0.88rem', marginBottom: 7, lineHeight: 1.45 }}>
                        <span style={{ fontWeight: 800, color }}>{st.stepNumber || i + 1}.</span>{' '}
                        {st.title ? <strong>{st.title} — </strong> : null}{renderInstruction(st.instruction, ai.tools, color)}
                        {st.acceptanceCriteria ? <div style={{ fontSize: '0.8rem', marginTop: 2 }}><strong style={{ color }}>✓ Accept:</strong> {st.acceptanceCriteria}</div> : null}
                        {st.requirementRef?.requirementId ? <div style={{ fontSize: '0.76rem', color: colors.muted, marginTop: 2 }}>REQ {st.requirementRef.documentNumber}{st.requirementRef.sectionNumber ? ` · ${st.requirementRef.sectionNumber}` : ''}{st.requirementRef.title ? ` — ${st.requirementRef.title}` : ''}</div> : null}
                        {st.caution ? <div style={{ color: colors.danger, fontSize: '0.8rem', marginTop: 2 }}>⚠ {st.caution}</div> : null}
                        {st.imageUrl ? <img src={st.imageUrl} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginTop: 4 }} /> : null}
                      </div>
                    ))}
                </div>
              ))}
            </div>
          )}

          {(station.procedures || []).concat(station.jigs || [], station.equipment || [], station.testProcedures || []).length > 0 && (
            <div style={card}>
              <div style={sectionTitle}>Tools, procedures & tests</div>
              <Resource label="Procedure" items={station.procedures} />
              <Resource label="Jig" items={station.jigs} />
              <Resource label="Equipment" items={station.equipment} />
              <Resource label="Test" items={station.testProcedures} />
            </div>
          )}

          {[station.entryGate, station.exitGate].filter(Boolean).map((g, gi) => (
            <div key={gi} style={card}>
              <div style={sectionTitle}>{gi === 0 && station.entryGate ? 'Entry gate' : 'Exit gate'} ({g.logic || 'AND'})</div>
              {(g.conditions || []).map((c, i) => (
                <div key={i} style={{ fontSize: '0.88rem', marginBottom: 3 }}>☐ {c.label || `${c.field} ${c.operator} ${c.value}`}</div>
              ))}
              {(g.routes || []).map((r, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: r.action === 'rework' ? colors.danger : colors.primary }}>
                  {r.action === 'rework' ? '↺' : '→'} {r.action.toUpperCase()} → {r.targetStationId}{r.label ? ` — ${r.label}` : ''}
                </div>
              ))}
            </div>
          ))}

          {(station.dependencies || []).length > 0 && (
            <div style={card}>
              <div style={sectionTitle}>Depends on</div>
              <div style={{ fontSize: '0.88rem' }}>{(station.dependencies || []).map(d => d.fromStationId).join(', ')}</div>
            </div>
          )}

          {(station.outputs || []).length > 0 && (
            <div style={card}>
              <div style={sectionTitle}>Produces</div>
              <div style={{ fontSize: '0.88rem' }}>{(station.outputs || []).map(o => o.name).join('; ')}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Resource({ label, items }) {
  if (!items || !items.length) return null;
  return (
    <>
      {items.map((it, i) => (
        <div key={i} style={{ fontSize: '0.86rem', marginBottom: 3 }}>
          <span style={{ color: colors.muted }}>{label}</span>{' '}
          <span style={{ fontFamily: 'monospace', color: '#60a5fa' }}>{it.documentNumber}</span>{' '}
          {it.name || it.title || ''}
        </div>
      ))}
    </>
  );
}
